import { useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { useUserAuthenticationContext, useViewportInfoContext } from "@/src/contexts";
import PageLayout from "@/src/shared/page-layout";
import { ShoppingMalls } from "@Routes/ShoppingMallLinks";

import ContentContainer, { type IContentContainer } from "../common/ContentContainer";
import ImageBanner, { type IImageBanner } from "../common/ImageBanner";
import { Unauthorized } from "../error-handling/Unauthorized/Unauthorized";
import { type IShoppingMallHeader, ShoppingMallHeader } from "./ShoppingMallHeader";
import { type IShoppingMallTabBrands, ShoppingMallTabBrands } from "./ShoppingMallTabBrands";
import { DayOfWeek, type IShoppingMallTabDetails, ShoppingMallTabDetails } from "./ShoppingMallTabDetails";
import { type IShoppingMallTabOffers, ShoppingMallTabOffers } from "./ShoppingMallTabOffers";
import { type IShoppingMallTabSaved, ShoppingMallTabSaved } from "./ShoppingMallTabSaved";

import * as cssPortal from "../shared/Portal.module.css";
import * as css from "./ShoppingMallDetails.module.css";

export interface IShoppingMallDetails {
    image: IImageBanner;
    location: ILocation;
    shoppingMallUrl: string;
    header: IShoppingMallHeader;
    tabs:
        | IShoppingMallTabOffers[]
        | IShoppingMallTabBrands[]
        | IShoppingMallTabDetails[]
        | IShoppingMallTabSaved[];
    isAuthenticated: boolean;
    ContentContainersOffers?: IContentContainer;
    ContentContainersBrands?: IContentContainer;
    ContentContainersDetails?: IContentContainer;
    ContentContainersSaved?: IContentContainer;
    initialData: IShoppingMallDetailsResult;
}

interface IShoppingMallDetailsState {
    selectedTab: number;
    response: IShoppingMallDetailsResult | undefined;
    showUnauthorized: boolean;
}

interface IShoppingMallDetailsResult {
    isObserved: boolean;
    openHours: string;
}

interface ILocation {
    street: string;
    city: string;
    postalCode: string;
    country: string;
    latitude: number;
    longitude: number;
}

export const ShoppingMallDetails = (props: IShoppingMallDetails) => {
    const { isAuthenticated } = useUserAuthenticationContext();
    const { pathname } = useLocation();
    const [search] = useSearchParams();
    const { isMobileOrTablet } = useViewportInfoContext();

    const [state, setState] = useState<IShoppingMallDetailsState>(() => {
        const selectedTab = Number(search.get("tab"));

        return {
            selectedTab: selectedTab && selectedTab < 4 ? selectedTab : 0,
            response: props.initialData,
            showUnauthorized: false,
            isObserved: false,
        };
    });

    const closeUnauthorized = () => setState(() => ({ ...state, showUnauthorized: false }));

    const onTabChange = (index: number): void => {
        if (index === 3 && isAuthenticated === false) {
            setState({ ...state, showUnauthorized: true });
            return;
        }

        setState({ ...state, selectedTab: index });
    };

    const jsonLdString = useMemo(() => {
        const detailsTab = props.tabs && (props.tabs[2] as IShoppingMallTabDetails);

        const name = props.header && props.header.name ? props.header.name : "";
        const logo =
            props.header && props.header.logo && props.header.logo.logo ? props.header.logo.logo.src : "";
        const location = props.location
            ? props.location
            : {
                  street: "",
                  city: "",
                  postalCode: "",
                  country: "",
                  latitude: "",
                  longitude: "",
              };

        const website = detailsTab && detailsTab.websiteLink ? detailsTab.websiteLink : "";

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const openDaysHelper = (): any[] => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let jsonld: any[] = [];
            if (detailsTab && detailsTab.openingHours && detailsTab.openingHours.length > 0) {
                let i = 0;
                while (i < detailsTab.openingHours[0].groupedDaysOpeningHours.length) {
                    const openingDays = detailsTab.openingHours[0].groupedDaysOpeningHours[i];
                    let reachedEnd = false;
                    let reachedBegin = false;
                    let weekDayNumber = 0;

                    let days: string[] = [];
                    while (!reachedEnd && weekDayNumber < 14) {
                        const weekDay = DayOfWeek[weekDayNumber % 7];
                        if (openingDays.firstDay === weekDay) {
                            reachedBegin = true;
                        }

                        if (reachedBegin === true) {
                            days = [...days, weekDay];

                            if (openingDays.lastDay === weekDay) {
                                reachedEnd = true;
                            }
                        }

                        weekDayNumber += 1;
                    }

                    jsonld = [
                        ...jsonld,
                        {
                            "@type": "OpeningHoursSpecification",
                            dayOfWeek: days,
                            opens: openingDays.openFrom,
                            closes: openingDays.openTo,
                        },
                    ];

                    i += 1;
                }
            }

            return jsonld;
        };

        const seoTags = {
            "@context": "http://schema.org",
            "@type": "ShoppingCenter",
            image: logo,
            "@id": props.shoppingMallUrl,
            name,
            address: {
                "@type": "PostalAddress",
                streetAddress: location.street,
                addressLocality: location.city,
                postalCode: location.postalCode,
                addressCountry: location.country,
            },
            geo: {
                "@type": "GeoCoordinates",
                latitude: location.latitude,
                longitude: location.longitude,
            },
            url: decodeURIComponent(website),
            openingHoursSpecification: openDaysHelper(),
        };

        return JSON.stringify(seoTags);
    }, []);

    const selectedTab = () => {
        const selectedTab = !props.tabs[state.selectedTab] ? null : { ...props.tabs[state.selectedTab] };

        if (!selectedTab) {
            return null;
        }

        switch (state.selectedTab) {
            case 1:
                return <ShoppingMallTabBrands {...(selectedTab as IShoppingMallTabBrands)} />;
            case 2:
                return <ShoppingMallTabDetails {...(selectedTab as IShoppingMallTabDetails)} />;
            default:
                if (state.selectedTab === 3 && !isMobileOrTablet)
                    return <ShoppingMallTabSaved {...(selectedTab as IShoppingMallTabSaved)} />;

                return <ShoppingMallTabOffers {...(selectedTab as IShoppingMallTabOffers)} />;
        }
    };

    return (
        <PageLayout
            breadcrumbs={[{ labelKey: "shoppingMall", path: ShoppingMalls }, { label: props.header.name }]}
            layoutAdUnitPath="galerie"
            slugTarget={pathname}
        >
            <div className={css.shoppingMallDetailsComponent}>
                <ImageBanner {...props.image} />
                <div className={cssPortal.standardContainer}>
                    <ShoppingMallHeader
                        {...props.header}
                        openHours={state.response ? state.response.openHours : undefined}
                        selectedTab={state.selectedTab}
                        onTabChange={onTabChange}
                    />
                    {state.showUnauthorized && <Unauthorized onClose={closeUnauthorized} />}
                    {selectedTab()}
                    <ContentContainer {...props.ContentContainersOffers} />
                    <ContentContainer {...props.ContentContainersBrands} />
                    <ContentContainer {...props.ContentContainersDetails} />
                    <ContentContainer {...props.ContentContainersSaved} />
                    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLdString }} />
                </div>
            </div>
        </PageLayout>
    );
};
